import React from "react";
import clsx from "clsx";
import { Camera, KeyRound, Mail, Phone } from "lucide-react";

import { Card } from "../../../shared/card/card.tsx";
import { Avatar } from "../../../ui/avatar/avatar.tsx";
import { Badge } from "../../../ui/badge/badge.tsx";
import { Button } from "../../../ui/button/button.tsx";
import { useSqeLabs } from "../../../../hooks/use-sqe-labs.ts";
import type { ProfileHeaderMock } from "../my-profile-mock.ts";

import styles from "./profile-header-card.module.css";

export interface ProfileHeaderCardProps {
  data: ProfileHeaderMock & {
    profileImage?: string;
    primaryEmail?: string;
    ccEmail?: string;
  };
  onChangePassword?: () => void;
  onAvatarClick?: () => void;
  className?: string;
}

export function ProfileHeaderCard({
  data,
  onChangePassword,
  onAvatarClick,
  className,
}: ProfileHeaderCardProps) {
  const isSqeLabs = useSqeLabs();
  const displayedEmail = isSqeLabs
    ? data.primaryEmail || "-"
    : data.ccEmail || data.email;

  return (
    <Card className={clsx(styles.card, className)} padding="7xl">
      <div className={styles.inner}>
        <div className={styles.avatarWrap}>
          {onAvatarClick ? (
            <button
              type="button"
              className={styles.avatarBtn}
              onClick={onAvatarClick}
              aria-label="Change Profile Photo"
            >
              <Avatar
                name={data.name}
                size="lg"
                src={data.profileImage}
              />
              <span className={styles.avatarOverlay} aria-hidden>
                <Camera size={18} strokeWidth={2} />
                <span className={styles.avatarOverlayText}>Change Photo</span>
              </span>
            </button>
          ) : (
            <Avatar
              name={data.name}
              size="xl"
              src={data.profileImage}
            />
          )}
        </div>
        <div className={styles.main}>
          <div className={styles.nameRow}>
            <h2 className="heading-xl">{data.name}</h2>
          </div>
          <p className={clsx("body-sm", styles.roleLine)}>
            <span>
              {data.role} · {data.department}
            </span>
            <span>
              <Badge variant="success" dot>
                {data.employeeType}
              </Badge>
            </span>
          </p>
          <div className={styles.metaRow}>
            <span className={clsx("body-xs", styles.metaItem)}>
              <Mail className={styles.metaIcon} strokeWidth={2} aria-hidden />
              {displayedEmail}
            </span>
            <span className={clsx("body-xs", styles.metaItem)}>
              <Phone className={styles.metaIcon} strokeWidth={2} aria-hidden />
              {data.phone}
            </span>

          </div>
        </div>
        <div className={styles.actions}>
          <Button
            type="button"
            variant="muted-outline"
            startIcon={<KeyRound size={16} strokeWidth={2} aria-hidden />}
            onClick={onChangePassword}
          >
            Change Password
          </Button>
        </div>
      </div>
      <div className={styles.divider}>
        <div className={styles.stats}>
          <div>
            <p className={clsx("sub-heading-sm", styles.statLabel)}>
              Employee ID
            </p>
            <p className={clsx("body-sm", styles.statValue)}>
              {data.employeeId}
            </p>
          </div>
          <div>
            <p className={clsx("sub-heading-sm", styles.statLabel)}>
              Department
            </p>
            <p className={clsx("body-sm", styles.statValue)}>
              {data.department}
            </p>
          </div>
          <div>
            <p className={clsx("sub-heading-sm", styles.statLabel)}>
              Date of Joining
            </p>
            <p className={clsx("body-sm", styles.statValue)}>{data.joinDate}</p>
          </div>
          <div>
            <p className={clsx("sub-heading-sm", styles.statLabel)}>
              Reporting Manager
            </p>
            <p className={clsx("body-sm", styles.statValue)}>
              {data.reportingTo}
            </p>
          </div>
        </div>
      </div>
    </Card>
  );
}
